import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import LeagueJoinPreview from "./LeagueJoinPreview";
import CreatedByBlock from "./CreatedByBlock";
import LeagueStatusLine from "./LeagueStatusLine";
import LeagueInviteGate from "./LeagueInviteGate";
import { INVITE_ERRORS, canPreview, resolveInviteToken } from "./inviteCode";
import { stripHtml } from "./leagueStatus";
import { scrollToTop } from "./wizard/wizardScroll";

const INVITE_TOAST_COPY = {
  [INVITE_ERRORS.notFound]: "Invite code not found. Check the code and try again.",
  [INVITE_ERRORS.started]: "Entry is closed.",
  [INVITE_ERRORS.offline]: "Could not connect. Try again.",
};

// The private league preview — where BOTH invite paths land.
//
//   Link: /contest/join/<INVITE_CODE>, which is what qushi.io/join/<CODE>
//         resolves to. The credential lives in the URL, so the page survives a
//         refresh and the organiser's link works on first open with nothing typed.
//   Code: the modal resolves a typed code, then navigates to the same URL.
//
// The URL segment may also be a plain league slug — someone was forwarded the
// address rather than the invite. That is NOT access: unless they're already a
// member, they get the invite gate instead of the league. See inviteCode.canPreview.
const PrivateLeaguePreview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Historically :slug; it now carries either credential, hence the name.
  const { slug: token } = useParams();
  const initialGame = location.state?.game || null;

  const [game, setGame] = useState(initialGame);
  const containerRef = useRef(null);
  // The invite link is the readable address plus the credential as ?c=CODE, so a
  // code in the query counts as arriving by invite — the organiser's link works
  // on first open with nothing typed, while the bare address still gates.
  const codeFromQuery = new URLSearchParams(location.search).get("c");
  // How the league was reached decides whether it may be shown at all.
  const [via, setVia] = useState(
    location.state?.via || (initialGame ? "code" : codeFromQuery ? "code" : null)
  );
  // Nothing to resolve and nothing handed over means the page was opened without
  // an invite at all — the guard below sends those back to League Play.
  const [loading, setLoading] = useState(!initialGame && !!token);

  useEffect(() => {
    if (game || !token) return;
    let cancelled = false;
    (async () => {
      // A credential in the query wins over the path, which is just the address.
      const result = await resolveInviteToken(codeFromQuery || token);
      if (cancelled) return;
      if (!result.ok) {
        // A dead link is the one case that redirects — there's no page to show.
        // Typed codes are handled inline, in the form.
        toast.error(INVITE_TOAST_COPY[result.message] || result.message);
        navigate("/contest", { replace: true });
        return;
      }
      // A public league is a real league, just not this screen's job.
      if (result.game.game_type !== "private") {
        navigate(`/contest/${result.game.vanity_url}`, { replace: true });
        return;
      }
      setGame(result.game);
      setVia(result.via);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [game, token, codeFromQuery, navigate]);

  // Client Rev 2: "When you enter that invite code and it takes you to the
  // private league landing page, does it drop you down the page or take you to
  // the top of the landing page? It drops me near the bottom, and all new screens
  // should load at the top."
  //
  // It does, and the reason is that the app does not scroll the WINDOW —
  // `.mainWrapper` is the scroller (see wizardScroll). The invite code is typed
  // in a modal on League Play, which by then is scrolled well down the page;
  // React Router swaps the route without touching that scroller, so the new
  // screen inherits the old screen's offset. `scrollToTop` walks up to the real
  // scrolling ancestor and resets it.
  //
  // It runs on `game` too, not only on mount: the code path arrives with the
  // league already in navigation state, but the LINK path mounts empty and
  // resolves the token first — the page grows from a one-line loader to the full
  // preview, and a reset done before that growth would be undone by it.
  useEffect(() => {
    scrollToTop(containerRef.current);
  }, [game]);

  if (!game && !loading) {
    navigate("/contest", { replace: true });
    return null;
  }

  // Reached by address rather than by invite — show nothing about the league.
  if (game && !canPreview(game, via)) return <LeagueInviteGate />;

  // The organiser's own one-liner, tags removed. Empty for a league that has none.
  const tagline = stripHtml(game?.tagline || "").trim();

  return (
    <div
      ref={containerRef}
      /* `pb-0` on mobile because the docked CTA below now reserves its own room,
         measured from the dock itself (LeagueJoinPreview). Keeping the old 48px
         on top of that reservation left a visible band of white between the last
         card and the bar at full scroll. Desktop has no dock and keeps it. */
      className="min-h-[calc(100vh-6rem)] pb-0 md:pb-12 bg-white md:bg-transparent"
    >
      <div className="max-w-screen-md mx-auto px-4 md:px-0 pt-2">
        {/* No in-page back arrow: on mobile the app header shows one in place of
            the avatar for this route (see DashboardHeader's path list), so a
            second one here would stack two arrows down the left edge. */}
        {/* ONE identity header for both breakpoints now.
            It used to be `md:hidden` — mobile put the name above the card while
            desktop kept it inside the centred summary card. Client Rev 2, desktop:
              • "Add YOU FOUND THE LEAGUE above the dynamic league title."
              • "Move the league title out of the centered summary card and place
                 it above the organizer image."
              • "Add the dynamic tagline directly below the league title."
              • "Left align the YOU FOUND THE LEAGUE / league title / tagline block
                 to the left edge of the main league content container."
            All four describe the block mobile already had, so the answer is to
            stop hiding it rather than to build a second one — the two can then
            never drift. It sits inside the same max-w-screen-md column the card
            does, so "the left edge of the main league content container" is the
            edge it starts on by construction.
            The card is told with `headerInHost` so it drops its own copy of the
            title and its hero badge. */}
        <div className="mb-3 md:mb-4">
          {/* Client Rev: "YOU FOUND THE LEAGUE needs to be in the same muted grey
              eyebrow color font that we use for all eyebrows, including PRIVATE
              LEAGUE & PUBLIC LEAGUE."
              It was not: this one had tracking-wider + text-secondary, while the
              PRIVATE LEAGUE / PUBLIC LEAGUE eyebrows in LeagueShell and
              PublicLeagueJoinCard both use tracking-[0.14em] + text-tertiary. So
              it read darker AND tighter than the eyebrow it sits beside on the
              very next screen. Same classes as those two now, character for
              character. */}
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-tertiary">
            You found the league
          </p>
          <h1 className="mt-0.5 font-montserrat font-bold text-title-subpage md:text-title-page text-pageTitle break-words">
            {game?.title || "Private league"}
          </h1>
          {/* Client Rev 2, mobile: "Replace the static line 'Your invite got you
              here. See what you are joining.' with the dynamic league tagline.
              This is correct and final." — and the same Rev deletes the earlier
              instruction about forcing that sentence onto two balanced lines,
              because the sentence itself is gone. Desktop: "Add the dynamic
              tagline directly below the league title." Same line, both widths.
              Rendered only when the league HAS a tagline; there is no static
              fallback to put back. */}
          {tagline && (
            <p className="mt-1 text-sm text-secondary break-words">{tagline}</p>
          )}

          {/* Client Rev 3, private league landing, mobile and desktop:
                "Move the CREATED BY identity block directly under the league title
                 and tagline. (like we have it in public).
                 Move the league status, such as Starts in 15h or Ends in 2d, to
                 the right side of the organizer handle row. Align it horizontally
                 with the organizer username.
                 Use this same header structure on both mobile and desktop."

              CREATED BY was at the BOTTOM of the summary card, under the stat
              strip and centred on desktop, and the status was a green clock line
              floating above the title. Neither matched the public landing, which
              has had exactly this row — identity left, countdown right, one line —
              since Rev 2. This is that row, built from the same two components the
              public one uses, so the two screens cannot drift again.

              `alignToHandle` is Rev 3 point 2 in the same breath: the status sits
              on the username's line rather than in the gap above it. See
              LeagueStatusLine.

              One structure at both widths, as asked — there is no `md:` here. */}
          {game && (
            <div className="mt-3 flex items-center justify-between gap-3">
              <CreatedByBlock game={game} />
              <LeagueStatusLine game={game} alignToHandle />
            </div>
          )}
        </div>

        {loading || !game ? (
          <p className="mt-10 text-center text-sm text-secondary">Loading league…</p>
        ) : (
          <LeagueJoinPreview game={game} titleInHeader headerInHost />
        )}
      </div>
    </div>
  );
};

export default PrivateLeaguePreview;
